import React from 'react';
import { UseFormRegister, UseFormSetValue, FieldErrors } from 'react-hook-form';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { BankDetails, BankingField } from '../../utils/types';
import {
  preventInvalidNameInput,
  preventInvalidNumberInput,
  preventInvalidNumberPaste,
  validateAccountNumber,
  validateIfscCode,
  formatIfscCode,
  validateRoutingNumber,
  validateSortCode,
  validateBsbNumber,
  validateIban,
  validateBicSwift,
  validateInstitutionNumber,
  validateTransitNumber,
} from '../../utils/validationMessages';

interface DynamicBankingFieldsProps {
  fields: BankingField[];
  register: UseFormRegister<BankDetails>;
  setValue: UseFormSetValue<BankDetails>;
  errors: FieldErrors<BankDetails>;
}

const ErrorMsg = ({ message }: { message?: string }) =>
  message ? (
    <p className="text-xs text-red-500 font-medium mt-1">{message}</p>
  ) : null;

const DynamicBankingFields: React.FC<DynamicBankingFieldsProps> = ({
  fields,
  register,
  setValue,
  errors,
}) => {
  if (!fields || fields.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {fields.map(field => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const fieldKey = field.key as any;
        const validationType = (field.validation || '').toUpperCase();
        const isRequired = field.required ?? false;
        const fieldError = errors[fieldKey]?.message as string | undefined;

        const validationRules: any = {};

        let onKeyDown:
          | ((e: React.KeyboardEvent<HTMLInputElement>) => void)
          | undefined;
        let onPaste:
          | ((e: React.ClipboardEvent<HTMLInputElement>) => void)
          | undefined;
        let onChangeTransform: ((val: string) => string) | undefined;
        let maxLength: number | undefined;
        let isMono = false;

        switch (validationType) {
          case 'ACCOUNT_NUMBER':
            onKeyDown = preventInvalidNumberInput;
            onPaste = preventInvalidNumberPaste;
            maxLength = 20;
            validationRules.validate = (value: string) => {
              if (!value && !isRequired) return true;
              const res = validateAccountNumber(value || '');
              return res.isValid ? true : res.error;
            };
            break;

          case 'IFSC':
            maxLength = 11;
            isMono = true;
            onChangeTransform = formatIfscCode;
            validationRules.validate = (value: string) => {
              if (!value && !isRequired) return true;
              const res = validateIfscCode(value || '');
              return res.isValid ? true : res.error;
            };
            break;

          case 'ROUTING_NUMBER':
            onKeyDown = preventInvalidNumberInput;
            onPaste = preventInvalidNumberPaste;
            maxLength = 9;
            isMono = true;
            validationRules.validate = (value: string) => {
              if (!value && !isRequired) return true;
              const res = validateRoutingNumber(value || '');
              return res.isValid ? true : res.error;
            };
            break;

          case 'SORT_CODE':
            maxLength = 8;
            isMono = true;
            validationRules.validate = (value: string) => {
              if (!value && !isRequired) return true;
              const res = validateSortCode(value || '');
              return res.isValid ? true : res.error;
            };
            break;

          case 'BSB':
          case 'BSB_NUMBER':
            maxLength = 7;
            isMono = true;
            validationRules.validate = (value: string) => {
              if (!value && !isRequired) return true;
              const res = validateBsbNumber(value || '');
              return res.isValid ? true : res.error;
            };
            break;

          case 'IBAN':
            isMono = true;
            onChangeTransform = (val: string) =>
              val.toUpperCase().replace(/\s/g, '');
            validationRules.validate = (value: string) => {
              if (!value && !isRequired) return true;
              const res = validateIban(value || '');
              return res.isValid ? true : res.error;
            };
            break;

          case 'BIC_SWIFT':
          case 'SWIFT':
          case 'BIC':
            isMono = true;
            onChangeTransform = (val: string) => val.toUpperCase().trim();
            validationRules.validate = (value: string) => {
              if (!value && !isRequired) return true;
              const res = validateBicSwift(value || '');
              return res.isValid ? true : res.error;
            };
            break;

          case 'INSTITUTION_NUMBER':
            onKeyDown = preventInvalidNumberInput;
            onPaste = preventInvalidNumberPaste;
            maxLength = 3;
            isMono = true;
            validationRules.validate = (value: string) => {
              if (!value && !isRequired) return true;
              const res = validateInstitutionNumber(value || '');
              return res.isValid ? true : res.error;
            };
            break;

          case 'TRANSIT_NUMBER':
            onKeyDown = preventInvalidNumberInput;
            onPaste = preventInvalidNumberPaste;
            maxLength = 5;
            isMono = true;
            validationRules.validate = (value: string) => {
              if (!value && !isRequired) return true;
              const res = validateTransitNumber(value || '');
              return res.isValid ? true : res.error;
            };
            break;

          default:
            if (isRequired) {
              validationRules.required = `${field.label} is required`;
            }
            if (field.type === 'number') {
              onKeyDown = preventInvalidNumberInput;
              onPaste = preventInvalidNumberPaste;
            } else if (field.key === 'branchName') {
              onKeyDown = preventInvalidNameInput;
            }
            break;
        }

        return (
          <div key={field.key} className="space-y-2">
            <Label htmlFor={field.key} className="text-sm font-semibold">
              {field.label} {isRequired ? '*' : ''}
            </Label>
            <Input
              id={field.key}
              type={field.type || 'text'}
              placeholder={field.placeholder || ''}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
              maxLength={maxLength}
              {...register(fieldKey, {
                ...validationRules,
                onChange: e => {
                  const rawVal = e.target.value;
                  const finalVal = onChangeTransform
                    ? onChangeTransform(rawVal)
                    : rawVal;
                  setValue(fieldKey, finalVal, { shouldValidate: true });
                },
              })}
              className={`${isMono ? 'font-mono' : ''} ${
                fieldError ? 'border-red-500 focus-visible:ring-red-500' : ''
              }`}
            />
            <ErrorMsg message={fieldError} />
          </div>
        );
      })}
    </div>
  );
};

export default DynamicBankingFields;
